<template>
  <div class="metric">
    <div class="metric-header">
      <div class="metric-title-wrap">
        <span class="metric-title">{{ title }}</span>
        <span
          v-if="status !== 'good'"
          class="status-icon"
          :class="{ warn: status === 'warn', bad: status === 'bad' }"
          :title="status === 'warn' ? 'Warning' : 'Critical'"
        >⚠</span>
      </div>
      <span class="metric-value">{{ value }}</span>
    </div>
    <div class="bar">
      <div
        class="bar-fill"
        :class="[colorClass, { grad: status === 'warn' }]"
        :style="{ width: clampedPercent + '%' }"
      />
    </div>
    <div class="bar-meta">{{ clampedPercent.toFixed(0) }}%</div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

export type MetricStatus = 'good' | 'warn' | 'bad';
export type MetricColorClass = 'mem' | 'cpu' | 'disk' | 'temp';

const props = defineProps<{
  title: string;
  value: string;
  percent: number;
  status: MetricStatus;
  colorClass: MetricColorClass;
}>();

const clampedPercent = computed(() => Math.max(0, Math.min(100, props.percent ?? 0)));
</script>

<style scoped lang="scss">
.metric {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.metric-title-wrap {
  display: inline-flex;
  align-items: center;
}

.metric-title {
  font-weight: 600;
  color: #374151;
}

.metric-value {
  color: #374151;
  font-size: 0.85rem;
}

.bar {
  height: 10px;
  background: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  transition: width 0.3s ease;

  &.mem { background: #00ffff; }
  &.mem.grad { background: linear-gradient(90deg, #00ffff 0%, #69e7ff 100%); }
  &.cpu { background: #baff5c; }
  &.cpu.grad { background: linear-gradient(90deg, #baff5c 0%, #00cf67 100%); }
  &.disk { background: #6d76ff; }
  &.disk.grad { background: linear-gradient(90deg, #6d76ff 0%, #0c00ad 100%); }
  &.temp { background: #6b1ab1; }
  &.temp.grad { background: linear-gradient(90deg, #6b1ab1 0%, #a420c8 100%); }
}

.status-icon {
  margin-left: 0.25rem;
  font-size: 1rem;
  line-height: 1;

  &.warn { color: #f59e0b; }
  &.bad { color: #dc2626; }
}

.bar-meta {
  color: #6b7280;
  font-size: 0.8rem;
}
</style>
